import {
  createAsyncThunk,
  createSlice,
  PayloadAction
} from "@reduxjs/toolkit";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { RootState } from "reducers";
import {
  getIndexById,
  getNextSortOrder,
  ISortParams,
  sort
} from "utils/sortUtils";
import { IGetCensusResponse } from "../../domain/IGetCensusResponse";
import { IGetResourcesListRequestParams } from "../../domain/IGetResourcesListRequestParams";
import { IResourceListItem } from "../../domain/IResourceListItem";
import { IResourceStatusResponse } from "../../domain/IResourceStatusResponse";
import { resourcesServices } from "../../services/resource.service";
import { updateResourceDetails } from "../Details/details.slice";
import { IRevers } from "../../domain/IRevers";

export const getResourcesList = createAsyncThunk(
  "Resources/getResourcesList",
  async (data: IGetResourcesListRequestParams) => {
    try {
      return await resourcesServices.getResourcesList(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getResourceStatusForPrint = createAsyncThunk(
  "Resources/getResourcesStatus",
  async (data: IGetResourcesListRequestParams) => {
    try {
      return await resourcesServices.getResourceStatus(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getResourcesCensusForPrint = createAsyncThunk(
  "Resources/getResourcesCensus",
  async () => {
    try {
      return await resourcesServices.getResourcesCensus();
    } catch (err) {
      setError(err);
    }
  }
);

export const getReverses = createAsyncThunk(
  "Resources/getReverses",
  async () => {
    try {
      const reversesResponse = await resourcesServices.getReversesList();
      return reversesResponse.reverses;
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteResource = createAsyncThunk(
  "Resources/deleteResource",
  async (resourceId: number) => {
    try {
      return await resourcesServices.deleteResource(resourceId);
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteReversPendingCharge = createAsyncThunk(
  "Resources/deleteReversPendingCharge",
  async (chargeId: number) => {
    try {
      await resourcesServices.deleteReversPendingCharge(chargeId);
      return chargeId;
    } catch (err) {
      setError(err);
    }
  }
)

interface mainState {
  loading: boolean;
  resources: IResourceListItem[];
  sort: ISortParams | undefined;
  resourceStatus?: IResourceStatusResponse;
  resourcesCensus?: IGetCensusResponse;
  reverses: IRevers[];
  selectedIndex: number;
  indexForDeletion: number;
  selectedReversIndex: number;
  newResourceModalOpen: boolean;
  deleteResourceModalOpen: boolean;
  showRefuelsAtDateModalOpen: boolean;
  newResourcePopover: IPopoverState;
  reversPopover: IPopoverState;
  searchPopover: IPopoverState;
  selectedResources: number[];
  error: any;
}

const initialState: mainState = {
  loading: false,
  resources: [],
  sort: undefined,
  resourceStatus: undefined,
  resourcesCensus: undefined,
  reverses: [],
  selectedIndex: 0,
  indexForDeletion: -1,
  selectedReversIndex: 0,
  newResourceModalOpen: false,
  deleteResourceModalOpen: false,
  showRefuelsAtDateModalOpen: false,
  newResourcePopover: { isOpen: false },
  reversPopover: { isOpen: false },
  searchPopover: { isOpen: false },
  selectedResources: [],
  error: null,
};

export const mainSlice = createSlice({
  name: "resources-main",
  initialState: initialState,
  reducers: {
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    setSelectedReversIndex: (state, action: PayloadAction<number>) => {
      state.selectedReversIndex = action.payload;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setNewResourceModalOpen: (state, action: PayloadAction<boolean>) => {
      state.newResourceModalOpen = action.payload;
    },
    setSearchResourcesPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopover = action.payload;
    },
    resetFilterResources: (state) => {
      state.selectedIndex = 0;
    },
    setDeleteResourceModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteResourceModalOpen = action.payload.visible;
      state.indexForDeletion = action.payload.index;
    },
    setShowRefuelsAtDateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.showRefuelsAtDateModalOpen = action.payload;
    },
    setNewResourcePopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newResourcePopover = action.payload;
    },
    setReversPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.reversPopover = action.payload;
    },
    setSelectedResources: (state, action: PayloadAction<{ id: number; value: boolean }>) => {
      const { id, value } = action.payload;
      const index = state.selectedResources.indexOf(id);

      if (value && index === -1) {
        state.selectedResources.push(id);
      } else if (!value && index !== -1) {
        state.selectedResources.splice(index, 1);
      }
    },
    setSort: (state, action: PayloadAction<ISortParams>) => {
      const newSortOrder = getNextSortOrder(state.sort, action.payload.key);
      const newSortParams = { key: action.payload.key, typeOfValue: action.payload.typeOfValue, order: newSortOrder };
      let selectedResourceId = undefined;
      if (state.resources.length > 0) {
        selectedResourceId = state.resources[state.selectedIndex].resourceId;
      }
      state.sort = newSortParams;
      const sortedResources = sort(state.resources, newSortParams);
      state.resources = sortedResources;
      state.selectedIndex = getIndexById(sortedResources, selectedResourceId, (resource: IResourceListItem) => resource.resourceId);
    },
    deleteResourceById: (
      state,
      action: PayloadAction<{ resourceId: number }>
    ) => {
      if (action.payload) {
        state.resources = state.resources.filter(
          (resource) => resource.resourceId !== action.payload.resourceId
        );
      }
    },
    resetStatusAfterPrint: (state) => {
      state.resourceStatus = undefined;
    },
    resetCensusAfterPrint: (state) => {
      state.resourcesCensus = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getResourcesList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getResourcesList.fulfilled, (state, action) => {
      if (action.payload) {
        if (state.sort) {
          state.resources = sort(action.payload, state.sort);
        } else {
          state.resources = action.payload;
        }
      } else {
        state.resources = [];
      }
      state.loading = false;
    });
    builder.addCase(getResourcesList.rejected, (state, action) => {
      state.error = action.error;
      state.loading = false;
    });
    builder.addCase(getResourceStatusForPrint.fulfilled, (state, action) => {
      state.resourceStatus = action.payload ? action.payload : undefined;
    });
    builder.addCase(getResourcesCensusForPrint.fulfilled, (state, action) => {
      state.resourcesCensus = action.payload ? action.payload : undefined;
    });
    builder.addCase(updateResourceDetails.fulfilled, (state, action) => {
      if (action.payload) {
        const updatedResource = action.payload.resource;
        for (let resourceIndex = 0; resourceIndex < state.resources.length; resourceIndex++) {
          if (state.resources[resourceIndex].resourceId === updatedResource.resourceId) {
            if (updatedResource.name !== state.resources[resourceIndex].name) {
              state.resources[resourceIndex].name = updatedResource.name;
            }
            if (updatedResource.status !== state.resources[resourceIndex].status) {
              state.resources[resourceIndex].status = updatedResource.status;
            }
            if (updatedResource.note !== state.resources[resourceIndex].note) {
              state.resources[resourceIndex].note = updatedResource.note;
            }
          }
        }

      }
    });
    builder.addCase(getReverses.pending, (state) => {
    });
    builder.addCase(getReverses.fulfilled, (state, action) => {
      state.reverses = action.payload ? action.payload : [];
    });
    builder.addCase(getReverses.rejected, (state, action) => {
      state.error = action.error;
    });
    builder.addCase(deleteReversPendingCharge.fulfilled, (state, action) => {
      state.reverses = state.reverses.map(revers => {
        const { charges, ...rest } = revers;
        return { charges: charges.filter(charge => charge.chargeId !== action.payload), ...rest };
      });
    });
  },
});

export const {
  setError,
  setSelectedIndex,
  setSelectedReversIndex,
  setNewResourceModalOpen,
  setSearchResourcesPopover,
  setSort,
  resetFilterResources,
  setDeleteResourceModalOpen,
  setShowRefuelsAtDateModalOpen,
  setNewResourcePopover,
  deleteResourceById,
  setReversPopover,
  setSelectedResources,
  resetStatusAfterPrint,
  resetCensusAfterPrint,
} = mainSlice.actions;

export const selectState = (state: RootState) => state.resources.main;
export const selectResource = (state: RootState) =>
  state.resources.main.resources[state.resources.main.selectedIndex];
export const selectResourceForDeletion = (state: RootState) =>
  state.resources.main.resources[state.resources.main.indexForDeletion];
export const selectRevers = (state: RootState) =>
  state.resources.main.reverses[state.resources.main.selectedReversIndex];
export default mainSlice.reducer;
